import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { isEmpty, isEqual } from 'lodash/lang';

import { updateStudyBudget as updateStudyBudgetAction } from '../../../../../actions/finance/budget/updateStudyBudgetAction';
import { FinBudgetApi, FinBudgetEventApi, FinTriggerApi, FinVendorApi } from '../../../../../api';
import NotificationManager from '../../../../../common/notifications/NotificationManager';
import { EXPORT_FAILED, PROTOCOL_SETUP_IS_INCOMPLETE } from '../../../../../constants/notificationMessages';
import { MANAGE_BUDGETS } from '../../../../../constants/userOperations';
import { userHasAccessTo } from '../../../../../services/auth';
import { toBill } from '../../../../../services/financial';
import { onBudgetFileSave, onRequestError } from '../../../../../services/handlers';

import { calculateSiteAmount } from './services';

export const BudgetDetailsContext = createContext(null);

export const BudgetDetailsContextProvider = ({ children }) => {
  const { id: budgetUniqueIdentifier } = useParams();
  const dispatch = useDispatch();

  const [state, setState] = useState({
    events: [],
    encounterTriggers: [],
    itemGroupTriggers: [],
    budget: {
      study: {
        name: ''
      },
      finClient: {
        name: ''
      },
      version: '',
      description: '',
      overhead: '',
      withholding: '',
      managementFee: '',
      comment: ''
    },
    isEditingForbidden: null,
    currentStudyBudget: null,
    previousUpdated: {}
  });

  const updateStudyBudget = useCallback(() => {
    const comment = state.budget?.comment?.trim();
    setState(prevState => ({ ...prevState, budget: { ...prevState.budget, comment } }));
    const updateRequest = {
      id: state.budget.id,
      description: state.budget.description,
      overhead: state.budget.overhead,
      withholding: state.budget.withholding,
      managementFee: state.budget.managementFee,
      comment
    };
    if (state.budget.description.length >= 500) {
      NotificationManager.error('Budget name cannot exceed 500 characters');
      return;
    }
    dispatch(updateStudyBudgetAction(updateRequest));
  }, [
    dispatch,
    state.budget?.comment,
    state.budget?.description,
    state.budget?.id,
    state.budget?.managementFee,
    state.budget?.overhead,
    state.budget?.withholding
  ]);

  const calculateSiteAmountWithManagementFee = useCallback((clientAmount, managementFeePercent) => {
    return +managementFeePercent === 0 ? 0 : calculateSiteAmount(clientAmount, managementFeePercent);
  }, []);

  const calculateElligoAmountWithManagementFee = useCallback(
    (budgetEvent, clientAmount, siteAmountWithManagementFee, managementFeePercent) => {
      const withManagementFeeCalculation =
        clientAmount - budgetEvent.patientAmount - toBill(siteAmountWithManagementFee) * 100 - budgetEvent.vendorAmount;
      if (budgetEvent.type === 'EXPENSE_VARIABLE') {
        return budgetEvent.managementFee && +managementFeePercent !== 0 ? withManagementFeeCalculation : 0;
      } else {
        return budgetEvent.managementFee ? withManagementFeeCalculation : budgetEvent.elligoAmount;
      }
    },
    []
  );

  const loadBudgetEvents = useCallback(() => {
    FinBudgetEventApi.findByBudget(budgetUniqueIdentifier).then(({ data }) => {
      setState(prevState => {
        const events = data?.map(budgetEvent => {
          if (budgetEvent.type === 'EXPENSE_VARIABLE' && budgetEvent.managementFee) {
            const siteAmount = calculateSiteAmountWithManagementFee(
              budgetEvent.maxAllowanceAmount,
              prevState.budget.managementFee
            );

            return {
              ...budgetEvent,
              siteAmount,
              elligoAmount: budgetEvent.maxAllowanceAmount - toBill(siteAmount) * 100
            };
          }

          return budgetEvent;
        });
        return { ...prevState, events };
      });
    }, onRequestError);
  }, [budgetUniqueIdentifier, calculateSiteAmountWithManagementFee]);

  const preloadModalDetails = useCallback(studyId => {
    FinTriggerApi.getEncounterTriggers(studyId).then(({ data }) => {
      setState(prevState => ({ ...prevState, encounterTriggers: data }));
    }, onRequestError);

    FinTriggerApi.getItemGroupTriggers(studyId).then(({ data }) => {
      setState(prevState => ({ ...prevState, itemGroupTriggers: data }));
      if (!userHasAccessTo(MANAGE_BUDGETS) && isEmpty(data)) {
        NotificationManager.error(PROTOCOL_SETUP_IS_INCOMPLETE);
      }
    }, onRequestError);

    FinVendorApi.findAllEnabled().then(({ data }) => {
      setState(prevState => ({ ...prevState, vendors: data }));
    }, onRequestError);
  }, []);

  useEffect(() => {
    const isBudgetStatusCorrectOrAbsent =
      !state.currentStudyBudget ||
      (state.currentStudyBudget && !isEqual(state.currentStudyBudget.uniqueIdentifier, budgetUniqueIdentifier));

    if (!isBudgetStatusCorrectOrAbsent && isEqual(state.isEditingForbidden, null)) {
      setState(prevState => ({ ...prevState, isEditingForbidden: !prevState.currentStudyBudget.editPossibility }));
    }
  }, [budgetUniqueIdentifier, state.currentStudyBudget, state.isEditingForbidden]);

  useEffect(() => {
    FinBudgetApi.find(budgetUniqueIdentifier).then(({ data }) => {
      setState(prevState => ({ ...prevState, budget: data, previousUpdated: data }));
      loadBudgetEvents();
      preloadModalDetails(data.studyId);
    }, onRequestError);

    FinBudgetApi.isEditable(budgetUniqueIdentifier).then(({ data }) => {
      setState(prevState => ({
        ...prevState,
        currentStudyBudget: { editPossibility: data, uniqueIdentifier: budgetUniqueIdentifier }
      }));
    });
  }, [budgetUniqueIdentifier, loadBudgetEvents, preloadModalDetails]);

  const onChangeTextVal = useCallback(({ target: { name, value } }) => {
    setState(prevState => {
      return { ...prevState, budget: { ...prevState.budget, [name]: value } };
    });
  }, []);

  const allRequiredFieldsAreFilledCorrectly = useCallback(() => {
    const { budget } = state;
    return (
      budget.description &&
      budget.description.trim() !== '' &&
      String(budget.overhead).match(/^(\d{1,2}\.\d{1}|\d{1,2}|100.0|100)$/) &&
      String(budget.withholding).match(/^(\d{1,2}\.\d{1}|\d{1,2}|100.0|100)$/) &&
      String(budget.managementFee).match(/^(\d{1,2}\.\d{1}|\d{1,2}|100.0|100)$/)
    );
  }, [state]);

  const onBlur = useCallback(
    ({ target: { name } }) => {
      if (allRequiredFieldsAreFilledCorrectly()) {
        if (state.previousUpdated[name] !== state.budget[name]) {
          setState(prevState => ({
            ...prevState,
            previousUpdated: { ...prevState.previousUpdated, [name]: prevState.budget[name] }
          }));
          updateStudyBudget();
        }
      }
    },
    [allRequiredFieldsAreFilledCorrectly, state.budget, state.previousUpdated, updateStudyBudget]
  );

  const onBlurManagementFee = useCallback(
    ({ target: { name, value } }) => {
      if (allRequiredFieldsAreFilledCorrectly()) {
        if (state.previousUpdated[name] !== state.budget[name]) {
          updateStudyBudget();
          setState(prevState => {
            return {
              ...prevState,
              previousUpdated: { ...prevState.previousUpdated, [name]: prevState.budget[name] },
              events: prevState.events.map(budgetEvent => {
                const managementFeePercent = value;
                const clientAmount =
                  budgetEvent.type === 'EXPENSE_VARIABLE' ? budgetEvent.maxAllowanceAmount : budgetEvent.clientAmount;
                const siteAmountWithManagementFee = calculateSiteAmountWithManagementFee(
                  clientAmount,
                  managementFeePercent
                );
                return {
                  ...budgetEvent,
                  siteAmount: budgetEvent.managementFee ? siteAmountWithManagementFee : budgetEvent.siteAmount,
                  elligoAmount: calculateElligoAmountWithManagementFee(
                    budgetEvent,
                    clientAmount,
                    siteAmountWithManagementFee,
                    managementFeePercent
                  ),
                  managementFee: +managementFeePercent === 0 ? false : budgetEvent.managementFee
                };
              })
            };
          });
        }
      }
    },
    [
      allRequiredFieldsAreFilledCorrectly,
      calculateElligoAmountWithManagementFee,
      calculateSiteAmountWithManagementFee,
      state?.budget,
      state?.previousUpdated,
      updateStudyBudget
    ]
  );

  const exportStudyBudget = useCallback(() => {
    FinBudgetApi.export(state.budget.id)
      .then(onBudgetFileSave)
      .catch(err => onRequestError(err, { customMessage: EXPORT_FAILED }));
  }, [state?.budget?.id]);

  const protocolIncomplete = useMemo(() => isEmpty(state.itemGroupTriggers) || isEmpty(state.encounterTriggers), [
    state.encounterTriggers,
    state.itemGroupTriggers
  ]);

  const value = useMemo(
    () => ({
      state,
      updateStudyBudget,
      allRequiredFieldsAreFilledCorrectly,
      exportStudyBudget,
      onChangeTextVal,
      onBlur,
      onBlurManagementFee,
      budgetUniqueIdentifier,
      loadBudgetEvents,
      protocolIncomplete
    }),
    [
      state,
      updateStudyBudget,
      allRequiredFieldsAreFilledCorrectly,
      exportStudyBudget,
      onChangeTextVal,
      onBlur,
      onBlurManagementFee,
      budgetUniqueIdentifier,
      loadBudgetEvents,
      protocolIncomplete
    ]
  );
  return <BudgetDetailsContext.Provider value={value}>{children}</BudgetDetailsContext.Provider>;
};

export const withBudgetDetailsContext = Component => {
  return props => {
    return (
      <BudgetDetailsContextProvider>
        <Component {...props} />
      </BudgetDetailsContextProvider>
    );
  };
};
